import type { ReactNode } from "react";

function H2({ children }: { children: ReactNode }) {
	return (
		<h2
			className="mt-10 font-mono text-[clamp(1.15rem,2.2vw,1.4rem)] leading-[1.2] tracking-[-0.01em] text-foreground"
			style={{ fontWeight: 400 }}
		>
			{children}
		</h2>
	);
}

function P({ children }: { children: ReactNode }) {
	return (
		<p className="mt-4 text-base leading-[1.65] text-muted-foreground">
			{children}
		</p>
	);
}

function A({ href, children }: { href: string; children: ReactNode }) {
	return (
		<a
			href={href}
			className="text-foreground underline underline-offset-4 transition-colors hover:text-brand"
		>
			{children}
		</a>
	);
}

function Note({ children }: { children: ReactNode }) {
	return (
		<div className="mt-6 border border-border bg-card p-4">
			<p className="text-base leading-[1.6] text-muted-foreground">
				{children}
			</p>
		</div>
	);
}

function Table({ head, rows }: { head: string[]; rows: string[][] }) {
	return (
		<div className="mt-6 overflow-x-auto">
			<table className="w-full min-w-[30rem] border-collapse font-mono text-sm">
				<thead>
					<tr>
						{head.map((cell) => (
							<th
								key={cell}
								className="border-b border-border px-3 py-2 text-left text-xs uppercase tracking-[0.1em] text-foreground"
							>
								{cell}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => (
						<tr key={row[0]}>
							{row.map((cell, i) => (
								<td
									key={cell}
									className={
										i === 0
											? "border-b border-border px-3 py-2 text-foreground"
											: "border-b border-border px-3 py-2 text-muted-foreground"
									}
								>
									{cell}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export function WatAiAanhaaltPost() {
	return (
		<>
			<P>
				Bouw je een product voor de Nederlandse markt, dan is er één cijfer dat
				je marketingbudget zou moeten sturen. Het staat in geen enkel Nederlands
				adviesartikel, omdat vrijwel alles wat je hier leest een vertaling is
				van een Amerikaanse bron.
			</P>
			<P>
				Als AI een Nederlandstalige koopvraag beantwoordt, komt 74,2% van de
				aangehaalde bronnen van een .nl-website. Bij internationale vragen is
				dat 2,7%.
			</P>

			<H2>Waar dat cijfer vandaan komt</H2>
			<P>
				<A href="https://promptradar.nl">PromptRadar</A> stelt gevolgde
				koopvragen op een vast ritme aan ChatGPT, Perplexity en Google AI Mode
				en bewaart elke bron die een antwoord aanhaalt. In augustus 2026 is de
				Nederlandse kant van dat archief apart geteld: 30 Nederlandse websites,
				40 scans, 4.988 citaties.
			</P>
			<Table
				head={["Type scan", "Citaties", "Naar een .nl-website"]}
				rows={[
					["Nederlandse website", "4.988", "74,2%"],
					["Internationale website", "17.075", "2,7%"],
				]}
			/>
			<P>
				Dat percentage is bovendien conservatief. De telling gaat op
				domeinextensie, dus Nederlandstalige pagina's op nl.trustpilot.com en
				nl.linkedin.com vallen buiten de 74,2%. Het echte aandeel
				Nederlandstalige bronnen ligt hoger.
			</P>
			<P>Per engine loopt het uiteen:</P>
			<Table
				head={["Engine", "Citaties", "Naar een .nl-website"]}
				rows={[
					["Perplexity", "2.437", "76,9%"],
					["Google AI Mode", "1.623", "76,5%"],
					["ChatGPT", "928", "62,8%"],
				]}
			/>
			<P>
				Perplexity en Google AI Mode leveren samen 81% van alle Nederlandse
				citaties. ChatGPT is het meest internationaal van de drie. Verkoop je
				aan Nederlandse klanten en kom je vooral in ChatGPT niet voor, dan
				concurreer je daar met buitenlandse bronnen die de vraag algemener
				beantwoorden.
			</P>

			<H2>Het advies dat je overal leest, klopt hier niet</H2>
			<P>
				Nu het deel dat het meeste geld scheelt. Elke aangehaalde pagina is
				geclassificeerd op paginatype. Naast de Nederlandse verdeling staat
				dezelfde meting over het hele archief van 21.508 citaties.
			</P>
			<Table
				head={["Paginatype", "Nederlandse scans", "Heel archief"]}
				rows={[
					["Homepage", "21,9%", "14,6%"],
					["Productpagina", "21,6%", "12,0%"],
					["Directory of gids", "13,3%", "-"],
					["Lijstje of vergelijking", "3,7%", "17,1%"],
				]}
			/>
			<P>
				Over het hele archief is een lijstje het meest aangehaalde paginatype,
				met 17,1%. Bij Nederlandse vragen is het 3,7%. Wat hier wordt aangehaald
				is je eigen homepage en je eigen productpagina's, samen goed voor 43,5%,
				plus directories en gidsen.
			</P>
			<P>
				Het standaardadvies is: zorg dat je in de roundups van anderen komt. Dat
				advies komt uit Engelstalige data en het is voor de Nederlandse markt
				bijna omgekeerd. Het budget dat je hier aan plaatsing in andermans
				vergelijking uitgeeft, levert in de meeste gevallen meer op als je het
				aan je eigen pagina's besteedt.
			</P>
			<Note>
				Eerlijk over de grenzen van deze meting. Het gaat om 30 Nederlandse
				websites en 40 scans, dus dit is een richting en geen volkstelling. De
				steekproef leunt op mkb: beveiliging, facilitair, bakkerijen, retail,
				zorg en een paar mediawebsites. 376 van de 4.988 citaties konden niet
				worden geclassificeerd. Een Nederlandse SaaS met een internationale
				markt zal andere cijfers zien.
			</Note>

			<H2>Wat je er morgen mee doet</H2>
			<P>
				<strong className="text-foreground">
					Zet het antwoord op je eigen pagina.
				</strong>{" "}
				Wat je product doet, wat het kost, waar je actief bent en voor wie het
				is. Niet in een pdf, niet drie klikken diep, maar op de homepage en de
				productpagina in tekst die de vraag letterlijk beantwoordt.
			</P>
			<P>
				<strong className="text-foreground">
					Kies je engines op basis van je markt.
				</strong>{" "}
				Verkoop je in Nederland, dan zijn Perplexity en Google AI Mode samen 81%
				van je zichtbaarheid. Een tool die Google AI Mode als betaalde add-on
				verkoopt, dekt je markt niet, en dat geldt voor meer aanbieders dan je
				zou denken.
			</P>
			<P>
				<strong className="text-foreground">
					Meet met een vaste vragenlijst.
				</strong>{" "}
				Schrijf tien vragen op zoals je klant ze typt, de helft met je merknaam
				en de helft zonder. Stel ze in het Nederlands in een uitgelogde sessie
				en noteer de antwoorden met datum. Verander die lijst daarna niet meer,
				anders meet je elke maand iets anders.
			</P>
			<P>
				Wil je de herhaalbare versie in plaats van een handmatige check, dan
				doet <A href="https://promptradar.nl">PromptRadar</A> dit voor
				Nederlandse websites: de koopvragen worden in het Nederlands gegenereerd
				en niet uit het Engels vertaald, je ziet elk AI-antwoord voluit met de
				merken die erin genoemd worden, en de eerste scan is gratis.
			</P>
		</>
	);
}
